import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { Alert, AppState, Platform } from "react-native";
import * as ScreenOrientation from "expo-screen-orientation";
import { reinitializeAuthAfterLegacyMigration } from "@src/features/auth";
import { handleAppModeTransition } from "@src/features/auth/utils/legacy-storage-migration";
import { presentLegacyAppWithCredentials } from "@src/features/auth/utils/legacy-kit-auth";
import { fetchFlags, getBooleanFlag, FEATURE_FLAGS, initFeatureFlags } from "@src/services/feature-flags/feature-flags";
import LegacyKitModule from "modules/legacy-kit";

export type LegacyAppMode = boolean | null;

export interface UseLegacyAppModeOptions {
  /** Skip re-presenting legacy when true (e.g. modal over legacy). Prevents onResume loop on Android. */
  showHolidayReminderRef?: RefObject<boolean>;
}

export interface UseLegacyAppModeReturn {
  shouldShowReactNativeScreens: LegacyAppMode;
  setShouldShowReactNativeScreens: (value: LegacyAppMode) => void;
  featureFlagsFailedToLoad: boolean;
  setFeatureFlagsFailedToLoad: (value: boolean) => void;
  handleRetryFeatureFlags: () => Promise<void>;
  handlePresentLegacyApp: () => Promise<void>;
}

/** Typed facade — single cast for the untyped native module. */
const legacyKit = LegacyKitModule as unknown as {
  dismissLegacyApp(): Promise<void>;
  addListener(event: string, cb: () => void): { remove: () => void };
};

/**
 * Legacy vs A3S app mode: state, foreground re-evaluation
 * (AppState + onResume on Android), present/dismiss legacy.
 */
export function useLegacyAppMode(options: UseLegacyAppModeOptions = {}): UseLegacyAppModeReturn {
  const { showHolidayReminderRef } = options;
  const [shouldShowReactNativeScreens, setShouldShowReactNativeScreens] = useState<LegacyAppMode>(null);
  const [featureFlagsFailedToLoad, setFeatureFlagsFailedToLoad] = useState(false);

  // Sync snapshot for async callbacks — updated atomically with state to avoid stale-ref window.
  const modeRef = useRef<LegacyAppMode>(null);
  const setMode = useCallback((next: LegacyAppMode) => {
    modeRef.current = next;
    setShouldShowReactNativeScreens(next);
  }, []);

  const handlePresentLegacyApp = useCallback(async () => {
    try {
      await presentLegacyAppWithCredentials();
    } catch (error) {
      Alert.alert("Error", `Failed to present legacy app: ${error}`);
    }
  }, []);

  const handleRetryFeatureFlags = useCallback(async () => {
    const success = await initFeatureFlags();
    if (success) setFeatureFlagsFailedToLoad(false);
  }, []);

  const runForegroundCheck = useCallback(
    async (fromLegacyActivityResumed = false) => {
      const current = modeRef.current;
      if (current === null) return;

      try {
        await fetchFlags();

        const showLegacy = getBooleanFlag(FEATURE_FLAGS.SHOW_LEGACY_APP_ON_STARTUP);
        console.log(`[useLegacyAppMode] foreground: showLegacy=${showLegacy}, current=${current}`);

        if (showLegacy && current === true) {
          await ScreenOrientation.unlockAsync();
          setMode(false);
          await handlePresentLegacyApp();
          return;
        }

        if (!showLegacy && current === false) {
          const migrated = await handleAppModeTransition(false);
          if (migrated) await reinitializeAuthAfterLegacyMigration();
          await legacyKit.dismissLegacyApp();
          await ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP);
          setMode(true);
          return;
        }

        // Android: re-present legacy after returning from RN activity overlay
        if (
          !fromLegacyActivityResumed &&
          Platform.OS === "android" &&
          current === false &&
          !showHolidayReminderRef?.current
        ) {
          await handlePresentLegacyApp();
        }
      } catch (error) {
        // Don't mutate mode — ref must match actual UI or future transitions break.
        console.warn("[useLegacyAppMode] Foreground check error:", error);
      }
    },
    [handlePresentLegacyApp, setMode, showHolidayReminderRef],
  );

  useEffect(() => {
    const appState = AppState.addEventListener("change", (next) => {
      if (next === "active") void runForegroundCheck();
    });

    const legacyResumed =
      Platform.OS === "android" ?
        legacyKit.addListener("onLegacyActivityResumed", () => void runForegroundCheck(true))
      : null;

    return () => {
      appState.remove();
      legacyResumed?.remove();
    };
  }, [runForegroundCheck]);

  return {
    shouldShowReactNativeScreens,
    setShouldShowReactNativeScreens: setMode,
    featureFlagsFailedToLoad,
    setFeatureFlagsFailedToLoad,
    handleRetryFeatureFlags,
    handlePresentLegacyApp,
  };
}
